import React, { useState } from 'react'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'
import OperatorLayout from '@/layouts/OperatorLayout'
import PricingRulesNav from '@/components/pricing-rules/PricingRulesNav'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { currencySymbol } from '@/lib/calendar-utils'
import SeasonForm from '@/components/pricing-rules/SeasonForm'
import type { SeasonFormDefaults } from '@/components/pricing-rules/SeasonForm'
import SeasonTemplateSelector from '@/components/pricing-rules/SeasonTemplateSelector'
import type { SelectedTemplate } from '@/components/pricing-rules/SeasonTemplateSelector'
import SeasonCalendarPreview from '@/components/pricing-rules/SeasonCalendarPreview'
import {
  visibleSeasonSuggestions,
  monthRangeLabel,
  formatSignedPct,
} from '@/lib/suggestions'
import { Plus, Pencil, Trash2, Calendar, BookTemplate, Sparkles } from 'lucide-react'
import type { SharedProps, SeasonSuggestion, SeasonSuggestions } from '@/types/shared'

interface Season {
  id: number
  name: string
  startDate: string
  endDate: string
  modifierType: string
  modifierValue: number
  priority: number
}

interface SeasonsProps extends SharedProps {
  seasons: Season[]
  seasonSuggestions: SeasonSuggestions
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatModifier(type: string, value: number): string {
  if (type === 'percentage') {
    return `${value > 0 ? '+' : ''}${value}%`
  }
  return `${value > 0 ? '+' : ''}${value} GBP`
}

export default function Seasons({
  currentUser,
  property,
  properties,
  seasons,
  seasonSuggestions,
}: SeasonsProps) {
  const [formOpen, setFormOpen] = useState(false)
  const [editingSeason, setEditingSeason] = useState<Season | null>(null)
  const [formDefaults, setFormDefaults] = useState<SeasonFormDefaults | null>(null)
  const [templateOpen, setTemplateOpen] = useState(false)
  const [deleting, setDeleting] = useState<number | null>(null)
  const review = usePricingReview()

  const base = property.basePath

  function handleAdd() {
    setEditingSeason(null)
    setFormDefaults(null)
    setFormOpen(true)
  }

  function handleEdit(season: Season) {
    setFormDefaults(null)
    setEditingSeason(season)
    setFormOpen(true)
  }

  // The server review names the season, its dates and the hotel before anything is deleted; it
  // replaces the browser's confirm() (change safety §5.17.1).
  function handleDelete(season: Season) {
    review.open(`${base}/seasons/review`, { operation: 'delete', id: season.id }, () =>
      inertiaSave('delete', `${base}/seasons/${season.id}`, {}, {
        onStart: () => setDeleting(season.id),
        onFinish: () => setDeleting(null),
      })
    )
  }

  function handleTemplateSelect(template: SelectedTemplate) {
    setEditingSeason(null)
    setFormDefaults({
      name: template.name,
      startDate: template.startDate,
      endDate: template.endDate,
      modifierType: template.modifierType,
      modifierValue: template.modifierValue,
      priority: template.priority,
    })
    setFormOpen(true)
  }

  // "Review & add" hands the suggestion's form-ready fields to the existing
  // SeasonForm as defaults — the operator can edit everything before it saves
  // through the normal create path. Nothing is ever applied automatically.
  function handleSuggestionReview(s: SeasonSuggestion) {
    setEditingSeason(null)
    setFormDefaults({
      name: s.name,
      startDate: s.startDate,
      endDate: s.endDate,
      modifierType: s.modifierType,
      modifierValue: s.modifierValue,
      priority: s.priority,
    })
    setFormOpen(true)
  }

  // Presentation-level filter: a suggestion stops rendering once any of its
  // months is covered by a season the operator already has (whether it came
  // from this section or was added by hand).
  const suggestionsToShow = visibleSeasonSuggestions(seasonSuggestions.seasons, seasons)

  return (
    <OperatorLayout title="Seasons" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <PricingRulesNav
          propertyBasePath={property.basePath}
          activeTab="seasons"
          help={{
            content:
              'Seasonal modifiers adjust rates for specific date ranges. Higher priority seasons take precedence when dates overlap.',
            learnMore: '/guides/seasons',
          }}
        />

        <div className="animate-slide-in space-y-6" style={{ animationDelay: '100ms' }}>
          {/* Header with Add buttons */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Seasonal Adjustments</h2>
              <p className="text-xs text-muted-foreground">
                Define seasonal pricing modifiers that adjust base rates for specific date ranges.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setTemplateOpen(true)} className="rounded-lg">
                <BookTemplate className="mr-2 h-4 w-4" />
                Add from Template
              </Button>
              <Button onClick={handleAdd} className="rounded-lg">
                <Plus className="mr-2 h-4 w-4" />
                Add Season
              </Button>
            </div>
          </div>

          {/* Seasons grid */}
          {seasons.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {seasons.map((season) => (
                <div
                  key={season.id}
                  className="group rounded-xl border border-border/60 bg-white p-4 shadow-sm transition-all hover:shadow-md"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1.5">
                      <h3 className="text-sm font-semibold text-foreground">{season.name}</h3>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {formatDate(season.startDate)} &ndash; {formatDate(season.endDate)}
                      </div>
                    </div>
                    <Badge
                      variant={season.modifierValue >= 0 ? 'default' : 'secondary'}
                      className={cn(
                        'text-xs font-semibold',
                        season.modifierValue > 0 && 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
                        season.modifierValue < 0 && 'bg-rose-100 text-rose-700 hover:bg-rose-100',
                        season.modifierValue === 0 && 'bg-slate-100 text-slate-600 hover:bg-slate-100'
                      )}
                    >
                      {formatModifier(season.modifierType, season.modifierValue)}
                    </Badge>
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      Priority {season.priority}
                    </span>
                    <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 rounded-lg p-0"
                        onClick={() => handleEdit(season)}
                        aria-label={`Edit ${season.name}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 rounded-lg p-0 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(season)}
                        disabled={deleting === season.id}
                        aria-label={`Delete ${season.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center">
              <Calendar className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No seasons configured</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Add a season to apply date-based pricing adjustments.
              </p>
              <div className="mt-4 flex justify-center gap-2">
                <Button variant="outline" className="rounded-lg" onClick={() => setTemplateOpen(true)}>
                  <BookTemplate className="mr-2 h-4 w-4" />
                  Add from Template
                </Button>
                <Button className="rounded-lg" onClick={handleAdd}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Your First Season
                </Button>
              </div>
            </div>
          )}

          {/* ── Suggested from booking history (P4) ─────────────────────────
              Backend-computed from the property's own trailing-12-month
              bookings; always operator-applied via SeasonForm, never
              auto-written. Values render exactly as sent — zero math here. */}
          {seasonSuggestions.reason === 'insufficient_data' && (
            <div
              className="animate-slide-in flex items-center gap-2 rounded-lg border border-border/40 bg-muted/30 px-4 py-3"
              style={{ animationDelay: '150ms' }}
            >
              <Sparkles className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
              <p className="text-xs text-muted-foreground">
                Not enough booking history yet to suggest seasons — check back as more stays
                accumulate.
              </p>
            </div>
          )}

          {suggestionsToShow.length > 0 && (
            <div className="animate-slide-in space-y-3" style={{ animationDelay: '150ms' }}>
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-semibold text-foreground">
                    Suggested from your booking history
                  </h2>
                  <HelpTooltip content="Patterns found in your last 12 months of bookings — months where guests paid noticeably more or less than your usual rate. Nothing is applied automatically: review a suggestion, adjust anything, and save it as a normal season." />
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Each one opens prefilled in the season form — review and edit before saving.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {suggestionsToShow.map((s) => (
                  <div
                    key={s.name}
                    className="rounded-xl border border-dashed border-primary/30 bg-primary/[0.03] p-4 transition-colors hover:border-primary/50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-1.5">
                        <h3 className="text-sm font-semibold text-foreground">{s.name}</h3>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {monthRangeLabel(s.startMonth, s.endMonth)}
                        </div>
                      </div>
                      <Badge
                        variant="secondary"
                        className={cn(
                          'text-xs font-semibold',
                          s.modifierPct > 0 && 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
                          s.modifierPct < 0 && 'bg-rose-100 text-rose-700 hover:bg-rose-100',
                          s.modifierPct === 0 && 'bg-slate-100 text-slate-600 hover:bg-slate-100'
                        )}
                      >
                        {formatSignedPct(s.modifierPct)}
                      </Badge>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Based on{' '}
                      <span className="tabular-nums font-medium text-foreground">
                        {s.sampleNights}
                      </span>{' '}
                      booked {s.sampleNights === 1 ? 'night' : 'nights'} in these months
                    </p>
                    <div className="mt-3 flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 rounded-lg text-xs"
                        onClick={() => handleSuggestionReview(s)}
                      >
                        <Plus className="mr-1.5 h-3 w-3" />
                        Review &amp; add
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Calendar Preview */}
          <div className="animate-slide-in" style={{ animationDelay: '200ms' }}>
            <SeasonCalendarPreview seasons={seasons} currencySymbol={currencySymbol(property.currency)} />
          </div>
        </div>

        {/* Season Template Selector Dialog */}
        <SeasonTemplateSelector
          propertyBasePath={property.basePath}
          open={templateOpen}
          onOpenChange={setTemplateOpen}
          onSelect={handleTemplateSelect}
        />

        {/* Season Form Dialog */}
        <SeasonForm
          propertyBasePath={property.basePath}
          season={editingSeason}
          defaults={formDefaults}
          open={formOpen}
          onOpenChange={setFormOpen}
        />
      </div>
      {review.dialog}
    </OperatorLayout>
  )
}
